import {
  useElementScrollRestoration,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Row,
  useReactTable,
} from "@tanstack/react-table";
import {
  notUndefined,
  observeElementRect,
  useVirtualizer,
} from "@tanstack/react-virtual";
import dayjs from "dayjs";
import React from "react";
import { useObjectRef } from "react-aria";

import { ActiveHoldIndicator, StatIndicator } from "@/components/Indicators";
import { StatusBadge } from "@/components/ui/icons";
import { Link } from "@/components/ui/link";
import { Skeleton } from "@/components/ui/skeleton";
import { Table } from "@/components/ui/table";
import { Text } from "@/components/ui/text";
import { useView } from "@/features/view/components/ViewProvider";
import {
  FocusManagerProvider,
  useFocusManager,
  useFocusManagerItem,
  useFocusManagerState,
  useVirtualNavigation,
} from "@/lib/focus-manager";
import type { TAccession } from "@/lib/types";
import { format } from "@/lib/utils";
import { useNavLocationState } from "@/utils/useNavStack";
import {
  TestPreviewHoverCard,
  TestPreviewProvider,
  useTestOrderPreview,
} from "./TestOrderPreview";

export function AccessionTable({
  accessions,
  isLoading,
  scrollRef,
  scrollRestorationId,
  selected,
}: {
  accessions: TAccession[];
  isLoading: boolean | undefined;
  scrollRef: React.RefObject<HTMLElement | null>;
  scrollRestorationId: string;
  selected?: string | number | null;
}) {
  const ref = React.useRef<HTMLTableElement>(null);

  const { itemRefs, containerProps, ...state } = useFocusManagerState(ref, {
    autoFocus: true,
    initialIndex: accessions.findIndex((acc) => acc.id === selected),
  });

  return (
    <FocusManagerProvider state={state}>
      <InnerTable
        ref={ref}
        isLoading={isLoading}
        accessions={accessions}
        scrollRef={scrollRef}
        scrollRestorationId={scrollRestorationId}
        selected={selected}
        {...containerProps}
      />
    </FocusManagerProvider>
  );
}

function InnerTable({
  accessions,
  isLoading,
  scrollRestorationId,
  scrollRef,
  selected,
  ref: tableRef,
  ...tableProps
}: {
  accessions: TAccession[];
  isLoading: boolean | undefined;
  scrollRestorationId: string;
  scrollRef: React.RefObject<HTMLElement | null>;
  selected?: string | number | null;
} & React.ComponentProps<"table">) {
  const { href, label } = useNavLocationState();
  const selectedIndex = accessions.findIndex((acc) => acc.id === selected);
  const { settings } = useView();
  const ref = useObjectRef(tableRef);

  const colDefs: ColumnDef<TAccession>[] = React.useMemo(() => {
    const defs: ColumnDef<TAccession>[] = [
      {
        id: "id",
        accessorKey: "id",
        header: "Accession ID",
        size: 130,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Link
              href={{
                to: "/accessions/$id",
                params: { id: row.original.id },
                state: {
                  href,
                  selected: row.original.id,
                  label,
                },
              }}
            >
              <Text mono size="1">
                {row.original.id}
              </Text>
            </Link>
            {row.original.hasHold && <ActiveHoldIndicator />}
            {row.original.priority === "STAT" && <StatIndicator />}
          </div>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        size: 120,
        cell: ({ getValue }) => <StatusBadge status={getValue() as "new"} />,
      },
      {
        id: "caseId",
        accessorKey: "caseId",
        header: "Donor ID",
        size: 130,
        cell: ({ getValue, row }) => (
          <Link
            href={{
              to: "/subjects/$id/accession",
              params: { id: row.original.subjectId },
              state: { href, selected: row.original.id, label },
            }}
          >
            <Text size="1" truncate className="hover:underline">
              {getValue<string>()}
            </Text>
          </Link>
        ),
      },
      {
        id: "unosId",
        accessorKey: "unosId",
        header: "UNOS ID",
        size: 100,
        cell: ({ getValue }) => (
          <Text size="1" truncate>
            {getValue<string>()}
          </Text>
        ),
      },
      {
        id: "client",
        accessorKey: "org.name",
        header: "Client",
        size: 160,
        cell: ({ getValue }) => (
          <Text size="1" truncate>
            {getValue<string>()}
          </Text>
        ),
      },
      {
        id: "testType",
        accessorKey: "testType",
        header: "Test type",
        size: 120,
        cell: ({ row }) =>
          row.original.testType ? (
            <TestPreviewHoverCard accession={row.original} />
          ) : (
            ""
          ),
      },
      {
        id: "date",
        accessorKey: "date",
        header: "Created",
        size: 140,
        cell: ({ getValue }) => (
          <Text size="1" muted>
            {settings.dateRelative === "actual"
              ? format(getValue<Date>())
              : dayjs(getValue<Date>()).fromNow()}
          </Text>
        ),
      },
    ];

    return defs;
  }, [settings.dateRelative, href, label]);

  const loadingData = React.useMemo(
    () => new Array(30).fill({}) as TAccession[],
    [],
  );

  const columns = useLoadingColumns<TAccession>(colDefs, isLoading);

  const { getFocusedIndex, setFocusedIndex } = useFocusManager();

  const table = useReactTable<TAccession>({
    data: isLoading ? loadingData : accessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  // scroll restoration offset, provided by tanstack router
  const scrollEntry = useElementScrollRestoration({
    id: scrollRestorationId,
  });

  const rowVirtualizer = useVirtualizer({
    count: isLoading ? loadingData.length : accessions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 46,
    getItemKey: (index) => accessions[index]?.id ?? index,

    observeElementRect(instance, cb) {
      setTimeout(() => {
        if (selectedIndex >= 0) {
          setFocusedIndex(selectedIndex);
        }
      }, 0);
      observeElementRect(instance, cb);
    },
    scrollPaddingEnd: 48,
    overscan: 10,
    initialOffset: scrollEntry?.scrollY,
  });

  // if data is cached by react-query, the data is available before the scrollRef is mounted
  // this will re-measure the table once the scrollRef is mounted
  React.useEffect(() => {
    if (scrollRef.current) {
      rowVirtualizer.measure();
    }
  }, [scrollRef.current, rowVirtualizer]);

  useVirtualNavigation({
    scrollToIndex: rowVirtualizer.scrollToIndex,
    getFocusedIndex,
    setFocusedIndex,
    items: accessions,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  // somehow this fixes sticky table headers with virtualizer
  // https://github.com/TanStack/virtual/issues/585#issuecomment-1716173260
  const [before, after] =
    virtualItems.length > 0
      ? [
          notUndefined(virtualItems[0]).start -
            rowVirtualizer.options.scrollMargin,
          rowVirtualizer.getTotalSize() -
            notUndefined(virtualItems[virtualItems.length - 1]).end,
        ]
      : [0, 0];

  return (
    <div style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
      <Table
        {...tableProps}
        ref={ref}
        className="border-separate border-spacing-0 table-fixed"
      >
        <Table.Header>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.Column
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{
                    height: 46,
                    width: header.getSize(),
                    minWidth: header.getSize(),
                  }}
                  className="border-b bg-background bg-opacity-50 backdrop-blur truncate sticky top-0 z-10"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </Table.Column>
              ))}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body>
          {before > 0 && (
            <tr>
              <td colSpan={colDefs.length} style={{ height: before }} />
            </tr>
          )}
          {virtualItems.map((virtualRow, _index) => (
            <TestPreviewProvider key={virtualRow.key}>
              <TableRow
                key={virtualRow.key}
                row={rows[virtualRow.index]}
                // the below is what's documented, however, doesn't seem to be required with
                // the before/after solution described above
                style={{
                  height: `${virtualRow.size}px`,
                  // transform: `translateY(${virtualRow.start - index * virtualRow.size}px)`,
                }}
              />
            </TestPreviewProvider>
          ))}
          {after > 0 && (
            <tr>
              <td colSpan={colDefs.length} style={{ height: after }} />
            </tr>
          )}
        </Table.Body>
      </Table>
    </div>
  );
}

function TableRow({
  row,
  style,
}: {
  row: Row<TAccession>;
  style?: React.CSSProperties;
}) {
  const { setOpen } = useTestOrderPreview();
  const ref = React.useRef<HTMLTableRowElement>(null);
  const navigate = useNavigate();
  const accession = row.original;
  const router = useRouter();
  const timerID = React.useRef<NodeJS.Timeout | null>(null);
  const { href, label } = useNavLocationState();

  const _preload = React.useCallback(() => {
    router.preloadRoute({
      to: "/accessions/$id",
      params: { id: accession.id },
    });
  }, [router, accession.id]);

  useFocusManagerItem(ref, row.index);

  return (
    <Table.Row
      style={style}
      // use outline instead of ring for safari
      className="group hover:bg-accent-muted focus-visible:outline-ring/70 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:rounded-md"
      ref={ref}
      onMouseEnter={() => {
        // timerID.current = setTimeout(() => {
        //   preload();
        // }, 200);
      }}
      onMouseLeave={() => {
        if (timerID.current) {
          clearTimeout(timerID.current);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === " ") {
          e.preventDefault();
          setOpen((flag) => !flag);
        } else if (e.key === "Enter") {
          navigate({
            to: "/accessions/$id",
            params: { id: accession.id },
            state: { href, selected: accession.id, label },
            startTransition: true,
          });
        } else {
          setOpen(false);
        }
      }}
      onClick={() => {
        navigate({
          to: "/accessions/$id",
          params: { id: accession.id },
          state: { href, selected: accession.id, label },
          startTransition: true,
        });
      }}
    >
      {row.getVisibleCells().map((cell) => (
        <Table.Cell
          key={cell.id}
          className="border-b group-focus-visible:border-transparent truncate py-0"
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </Table.Cell>
      ))}
    </Table.Row>
  );
}

function useLoadingColumns<T>(
  columns: ColumnDef<T>[],
  isLoading: boolean | undefined,
) {
  return React.useMemo(() => {
    if (!isLoading) return columns;
    return columns.map((col) => ({
      ...col,
      cell: () => <Skeleton className="h-4 w-20" />,
    }));
  }, [columns, isLoading]);
}
